/**
 * Data Table component.
 * Modern glass table with sticky blur headers and subtle hover.
 */

import type { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Skeleton,
} from '@mui/material';

/**
 * Data Table column definition
 */
export interface DataTableColumn<T = any> {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any, row: T) => ReactNode;
  render?: (row: T) => ReactNode;
}

/**
 * Data Table component props
 */
export interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[];
  rows: T[];
  loading?: boolean;
  emptyMessage?: string;
  emptyState?: ReactNode;
  stickyHeader?: boolean;
  maxHeight?: number | string;
  size?: 'small' | 'medium';
  striped?: boolean;
  hover?: boolean;
  onRowClick?: (row: T) => void;
}

/**
 * Data Table component with modern glassmorphism styling.
 */
const DataTable = <T extends Record<string, any>>({
  columns,
  rows,
  loading = false,
  emptyMessage = 'No data available',
  emptyState,
  stickyHeader = true,
  maxHeight,
  size = 'medium',
  striped: _striped = false,
  hover = true,
  onRowClick,
}: DataTableProps<T>) => {
  // Loading skeleton
  if (loading) {
    return (
      <Box
        sx={{
          borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
          border: '1px solid var(--glass-border)',
          overflow: 'hidden',
        }}
      >
        {/* Header skeleton */}
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid var(--glass-border)' }}>
          <Box sx={{ display: 'flex', gap: 4 }}>
            {columns.map((col) => (
              <Skeleton
                key={col.id}
                variant="text"
                width={col.minWidth || 100}
                height={20}
                sx={{ bgcolor: 'var(--glass-bg-subtle)' }}
              />
            ))}
          </Box>
        </Box>
        {/* Row skeletons */}
        {[...Array(5)].map((_, i) => (
          <Box key={i} sx={{ px: 3, py: 2, borderBottom: '1px solid var(--glass-border)' }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              {columns.map((col) => (
                <Skeleton
                  key={col.id}
                  variant="text"
                  width={col.minWidth || 100}
                  height={20}
                  sx={{ bgcolor: 'var(--glass-bg-subtle)' }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // Empty state
  if (rows.length === 0) {
    if (emptyState) {
      return <>{emptyState}</>;
    }
    return (
      <Box
        sx={{
          p: 8,
          textAlign: 'center',
          borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
          border: '1px solid var(--glass-border)',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight,
        borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
        border: '1px solid var(--glass-border)',
        boxShadow: 'none',
        backgroundImage: 'none',
        overflow: 'auto',
      }}
    >
      <Table stickyHeader={stickyHeader} size={size} sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                sx={{
                  minWidth: column.minWidth,
                  fontWeight: 600,
                  fontSize: '0.6875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--mui-palette-text-secondary)',
                  borderBottom: '1px solid var(--glass-border)',
                  py: 2,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              hover={hover}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'background-color 150ms ease',
                '&:last-child td': {
                  borderBottom: 0,
                },
                '&:hover': {
                  backgroundColor: 'var(--mui-palette-action-hover)',
                },
              }}
            >
              {columns.map((column) => {
                const value = row[column.id];
                return (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{
                      fontSize: '0.8125rem',
                      color: 'var(--mui-palette-text-primary)',
                      py: 2,
                      borderBottom: '1px solid var(--glass-border)',
                    }}
                  >
                    {column.render ? column.render(row) : column.format ? column.format(value, row) : value}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
